import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Field } from '@/components/ui/Field';
import { describedByIds, fieldControlClass, fieldIconData } from '@/components/ui/fieldClasses';
import { cn } from '@/utils/cn';

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));

function minuteValues(step: number): string[] {
  const values: string[] = [];
  const safeStep = step > 0 ? step : 1;
  for (let minute = 0; minute < 60; minute += safeStep) {
    values.push(String(minute).padStart(2, '0'));
  }
  return values;
}

export function parseTimeInputValue(value: string): { hour: string; minute: string } | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)(?::[0-5]\d)?$/.exec(value.trim());
  if (!match) return null;
  return { hour: match[1], minute: match[2] };
}

export const TimeInput = forwardRef<HTMLSelectElement, {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  id?: string;
  name?: string;
  label?: string;
  error?: string;
  hint?: string;
  required?: boolean;
  className?: string;
  minuteStep?: number;
  allowEmpty?: boolean;
}>(function TimeInput(
  {
    value,
    onChange,
    disabled,
    id,
    name,
    label,
    error,
    hint,
    required,
    className,
    minuteStep = 1,
    allowEmpty = false,
  },
  ref,
) {
  const { t } = useTranslation();
  const parsed = parseTimeInputValue(value);
  const hour = parsed?.hour ?? '';
  const minute = parsed?.minute ?? '';
  const minutes = minuteValues(minuteStep);
  const minuteOptions = minute && !minutes.includes(minute) ? [...minutes, minute].sort() : minutes;
  const inputId = id ?? name;
  const hintId = inputId ? `${inputId}-hint` : undefined;
  const errorId = inputId ? `${inputId}-error` : undefined;

  function emit(nextHour: string, nextMinute: string) {
    if (!nextHour && !nextMinute) {
      onChange('');
      return;
    }
    onChange(`${nextHour || '00'}:${nextMinute || '00'}`);
  }

  const control = (
    <div
      className={cn('inline-flex items-center gap-s1', className)}
      data-testid="time-input"
      data-value={parsed ? `${hour}:${minute}` : ''}
    >
      <select
        id={inputId}
        ref={ref}
        name={name ? `${name}-hour` : undefined}
        disabled={disabled}
        required={required && !allowEmpty}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={
          inputId
            ? describedByIds({
                id: inputId,
                error: Boolean(error),
                hint: Boolean(hint),
              })
            : undefined
        }
        aria-label={label ? `${label} (${t('common.hour')})` : t('common.hour')}
        className={fieldControlClass({ error: Boolean(error), className: 'w-[4.75rem]' })}
        {...fieldIconData({ trailing: true })}
        value={hour}
        onChange={(event) => emit(event.target.value, minute)}
      >
        {allowEmpty || !hour ? <option value="">{t('common.time_empty')}</option> : null}
        {HOURS.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
      <span className="text-ds-muted" aria-hidden>
        :
      </span>
      <select
        disabled={disabled}
        required={required && !allowEmpty}
        aria-label={label ? `${label} (${t('common.minute')})` : t('common.minute')}
        className={fieldControlClass({ error: Boolean(error), className: 'w-[4.75rem]' })}
        {...fieldIconData({ trailing: true })}
        value={minute}
        onChange={(event) => emit(hour, event.target.value)}
      >
        {allowEmpty || !minute ? <option value="">{t('common.time_empty')}</option> : null}
        {minuteOptions.map((item) => (
          <option key={item} value={item}>
            {item}
          </option>
        ))}
      </select>
    </div>
  );

  if (!label && !error && !hint) return control;

  return (
    <Field
      label={label}
      htmlFor={inputId}
      required={required}
      hint={hint}
      error={error}
      hintId={hintId}
      errorId={errorId}
    >
      {control}
    </Field>
  );
});
